import { useMemo } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { IoBookOutline } from "react-icons/io5";
import { BiSolidTimeFive } from "react-icons/bi";
import { Link } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { getLanguageDirection } from "../i18n/config";
import { ASSET_URI, fetchData, limitCharecters, PROXY } from "../lib/utils";
import useSWR from "swr";
import { FaRegCircleCheck } from "react-icons/fa6";

const Dashboard = () => {
  const { accessToken } = useAuth();
  const { t, i18n } = useTranslation();

  const fetcher = (url) => fetchData(url, accessToken);
  const { data, error, isLoading } = useSWR(
    `${PROXY}/courses/users`,
    fetcher,
  );

  const safeCourses = data || [];

  // ✅ DO NOT TOUCH your RTL/LTR logic (kept as you had it)
  const isRTL = getLanguageDirection(i18n.language) === "rtl";

  // ✅ Better title lookup without breaking your RTL/LTR:
  const langCode = useMemo(() => {
    const raw = String(i18n.language || "en")
      .toLowerCase()
      .replaceAll("_", "-");
    return raw.split("-")[0] || "en";
  }, [i18n.language]);

  const getCourseTitle = (course) =>
    limitCharecters(
      course?.title?.[i18n.language] ??
        course?.title?.[langCode] ??
        course?.title?.en ??
        "",
      26,
    );

  const getSubCategory = (course) =>
    course?.category?.subCategories?.[0]?.[i18n.language] ??
    course?.category?.subCategories?.[0]?.[langCode] ??
    course?.category?.subCategories?.[0]?.en ??
    "";

  if (error) return <div>Error: {error.message}</div>;
  if (isLoading) return <div>{t("loading")}</div>;

  const inProgressCount = safeCourses.filter(
    (course) => course.completionPercentage < 100,
  ).length;

  const completedCount = safeCourses.filter(
    (course) => course.completionPercentage === 100,
  ).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 dark:bg-gray-800 dark:border-gray-700 border-t border-t-gray-200 pt-2 mt-10">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-3 p-4">
            <div className="rounded-xl bg-indigo-50 p-3">
              <BiSolidTimeFive size={26} className="text-indigo-600" />
            </div>
            <div>
              <p className="font-bold text-lg text-gray-900 dark:text-white">
                {t("in-progress")}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {inProgressCount} {t("courses")}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-3 p-4">
            <div className="rounded-xl bg-indigo-50 p-3">
              <FaCheckCircle size={24} className="text-indigo-600" />
            </div>
            <div>
              <p className="font-bold text-lg text-gray-900 dark:text-white">
                {t("completed")}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {completedCount} {t("courses")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Empty */}
      {safeCourses.length === 0 ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <h1 className="text-xl lg:text-2xl font-bold text-indigo-500 text-center">
            {t("You-have-not-enrolled-in-any-courses")}
          </h1>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 my-7 px-2">
          {safeCourses.map((course) => {
            const pct = Number(course?.completionPercentage ?? 0);
            const isDone = pct >= 100;

            return (
              <div key={course._id} className="relative">
                <Link to={`/courses/${course._id}/current`} className="block">
                  <div className="group overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg dark:bg-gray-800 dark:border-gray-700">
                    <div className="relative">
                      <img
                        className="h-36 w-full object-cover"
                        src={`${ASSET_URI}/${course.image}`}
                        height="200"
                        width="200"
                        alt="book image"
                        crossOrigin="anonymous"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/0 to-black/0" />

                      <div
                        className={`absolute bottom-2 ${
                          isRTL ? "right-2" : "left-2"
                        } rounded-full px-2 py-1 text-xs font-semibold text-white backdrop-blur-sm ${
                          isDone ? "bg-indigo-600/90" : "bg-black/40"
                        }`}
                      >
                        {isDone ? t("completed") : `${pct.toFixed(0)}%`}
                      </div>
                    </div>

                    <div className="p-3">
                      <h2 className="text-[15px] font-bold text-gray-900 dark:text-white mt-1 leading-snug">
                        <span className="block truncate">
                          {getCourseTitle(course)}
                        </span>
                      </h2>

                      <small className="mt-1 block text-xs font-semibold tracking-tight text-gray-600 dark:text-gray-300">
                        <span className="block truncate">
                          {getSubCategory(course)}
                        </span>
                      </small>

                      <div className="mt-4 space-y-2">
                        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                          <IoBookOutline
                            size={16}
                            className="text-indigo-500"
                          />
                          <p className="text-xs font-semibold">
                            {course?.totalChapters} {t("total")} {t("videos")}
                          </p>
                        </div>

                        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                          <FaRegCircleCheck
                            size={16}
                            className="text-indigo-500"
                          />
                          <p className="text-xs font-semibold">
                            {course.completionPercentage === 100
                              ? `100% ${t("completed")}`
                              : `${course?.completedChapters} ${t(
                                  "watched-Chapters",
                                )}`}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                          <div
                            className={`h-2 rounded-full ${
                              isDone ? "bg-indigo-600" : "bg-indigo-500"
                            }`}
                            style={{ width: `${pct.toFixed(0)}%` }}
                          />
                        </div>

                        <div className="mt-1 flex items-center justify-between text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                          <span>
                            {pct.toFixed(0)}% {t("completed")}
                          </span>
                          <span className={isDone ? "text-indigo-600" : ""}>
                            {isDone ? t("completed") : t("in-progress")}
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
